import { getStatusColor } from '../../utils/appointmentUtils';
import { generateShortSlug } from '../../utils/helpers';
import { listAppointments, upsertAppointment, deleteAppointmentApi } from '../../api/appointments';

const convertToDate = (dateValue) => {
  if (!dateValue) return null;

  try {
    if (dateValue instanceof Date) {
      return dateValue;
    }

    if (dateValue?.toDate && typeof dateValue.toDate === 'function') {
      return dateValue.toDate();
    }

    if (dateValue?.seconds) {
      return new Date(dateValue.seconds * 1000);
    }

    const date = new Date(dateValue);
    return isNaN(date.getTime()) ? null : date;
  } catch (error) {
    return null;
  }
};

function mapApiAppointmentToCalendarEvent(appointment) {
  const dateTime = convertToDate(appointment.dateTime);
  const endTime = convertToDate(appointment.endTime) ||
    (dateTime && appointment.duration ? new Date(dateTime.getTime() + (appointment.duration * 60000)) : null);
  if (!dateTime || !endTime) return null;
  const status = appointment.status || 'pending';
  return {
    id: appointment.id,
    ...appointment,
    storageLocation: 'company',
    shortSlug: appointment.shortSlug || null,
    dateTime,
    endTime,
    color: appointment.color || getStatusColor(status),
    avatar: appointment.avatar || '',
    start: dateTime,
    end: endTime,
    title: appointment.clientName || 'Untitled',
    resource: {
      ...appointment,
      employeeId: appointment.employeeId,
      shortSlug: appointment.shortSlug || null,
      dateTime,
      endTime,
      status
    }
  };
}

async function listAppointmentsFromApi(companyId, { from, to } = {}) {
  const result = await listAppointments({ companyId, from, to });
  return Array.isArray(result?.appointments) ? result.appointments : [];
}

export const getAllAppointments = async (companyId) => {
  if (!companyId) {
    throw new Error('Company ID is required');
  }

  const apiAppointments = await listAppointmentsFromApi(companyId);
  return apiAppointments
    .map(mapApiAppointmentToCalendarEvent)
    .filter(Boolean);
};

export const getAppointmentsByDate = async (date, companyId) => {
  if (!companyId) {
    throw new Error('Company ID is required');
  }

  const start = new Date(date.getFullYear(), date.getMonth(), date.getDate());
  const end = new Date(start.getTime() + 24 * 60 * 60 * 1000 - 1);
  const apiAppointments = await listAppointmentsFromApi(companyId, { from: start, to: end });
  const requestedDay = start.getTime();
  return apiAppointments
    .map(mapApiAppointmentToCalendarEvent)
    .filter((appointment) => {
      if (!appointment) return false;
      const appointmentDay = new Date(
        appointment.start.getFullYear(),
        appointment.start.getMonth(),
        appointment.start.getDate()
      );
      return appointmentDay.getTime() === requestedDay;
    });
};

export const getAppointmentsByDateAndHour = async (date, hour, companyId) => {
  const dateAppointments = await getAppointmentsByDate(date, companyId);
  return dateAppointments.filter((appointment) => appointment.start.getHours() === hour);
};

export const getAppointmentCountsByHour = async (date, companyId) => {
  const dateAppointments = await getAppointmentsByDate(date, companyId);
  const hourCounts = {};
  for (let i = 0; i < 24; i++) {
    hourCounts[i] = 0;
  }
  dateAppointments.forEach((appointment) => {
    const h = appointment.start.getHours();
    hourCounts[h] = (hourCounts[h] || 0) + 1;
  });
  return hourCounts;
};

export const createAppointment = async (appointmentData, companyId) => {
  if (!companyId) {
    throw new Error('Company ID is required');
  }

  const id = crypto.randomUUID();
  const shortSlug = generateShortSlug();
  const payload = {
    ...appointmentData,
    id,
    companyId,
    shortSlug,
  };
  const result = await upsertAppointment({
    companyId,
    id,
    appointment: payload,
  });
  const saved = result?.appointment || payload;
  return {
    id,
    ...appointmentData,
    shortSlug: saved.shortSlug || shortSlug,
  };
};

export const updateAppointment = async (id, appointmentData, companyId) => {
  if (!companyId || !id) {
    throw new Error('Company ID and Appointment ID are required');
  }

  const result = await upsertAppointment({
    companyId,
    id,
    appointment: {
      ...appointmentData,
      id,
      companyId,
    },
  });
  const saved = result?.appointment || appointmentData;
  const mapped = mapApiAppointmentToCalendarEvent({ id, ...saved });
  if (mapped) return mapped;
  return {
    id,
    ...saved,
    dateTime: convertToDate(saved.dateTime),
    endTime: convertToDate(saved.endTime),
  };
};

export const deleteAppointment = async (id, companyId) => {
  if (!companyId || !id) {
    throw new Error('Company ID and Appointment ID are required');
  }

  await deleteAppointmentApi({ companyId, id });
  return { id };
};
